import { When } from "react-if";
import { GridRowSelectionModel } from "@mui/x-data-grid";

import { Button } from "../../../common/Atoms/Button";
import { Dropdown, DropdownOption } from "../../../common/Atoms/Dropdown";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { useCurrentOrgUsers } from "../../hooks/useCurrentOrgUsers";
import {
  useBulkAssignEscalationsMutation,
  useBulkCancelEscalationsMutation,
  useBulkCloseEscalationsMutation,
  useBulkFreezeEscalationsMutation,
  useBulkSnoozeEscalationsMutation,
} from "../../services/api/escalationApi/escalation";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";

const MAX_BATCH = 50;
const UNASSIGN = `__UNASSIGN__`;

const snoozeOptions: DropdownOption[] = [
  { label: `Tomorrow`, value: `1` },
  { label: `3 Days Later`, value: `3` },
  { label: `Next Week`, value: `7` },
  { label: `Next Month`, value: `30` },
];

interface ISelectedEscalationOptionsProps {
  rowSelectionModel: GridRowSelectionModel;
}

export function SelectedEscalationOptions({
  rowSelectionModel,
}: ISelectedEscalationOptionsProps) {
  const currentOrg = useGetSelectedOrganisation();
  const [bulkCloseApi, { isLoading: bulkCloseLoading }] = useBulkCloseEscalationsMutation();
  const [bulkCancelApi, { isLoading: bulkCancelLoading }] = useBulkCancelEscalationsMutation();
  const [bulkAssignApi, { isLoading: bulkAssignLoading }] = useBulkAssignEscalationsMutation();
  const [bulkSnoozeApi, { isLoading: bulkSnoozeLoading }] = useBulkSnoozeEscalationsMutation();
  const [bulkFreezeApi, { isLoading: bulkFreezeLoading }] = useBulkFreezeEscalationsMutation();
  const userOptions = useCurrentOrgUsers(true);

  const selectedEscalationIds = [...rowSelectionModel.ids] as number[];

  const escalationCountText = `${selectedEscalationIds.length} escalation${selectedEscalationIds.length === 1 ? `` : `s`}`;

  const disabled = selectedEscalationIds.length === 0 || selectedEscalationIds.length > MAX_BATCH;

  async function onClick(type: `close` | `cancel`) {
    if (currentOrg?.id) {
      if (type === `close`) {
        bulkCloseApi({
          organisationId: currentOrg.id,
          escalationIds: selectedEscalationIds,
        });
      }

      if (type === `cancel`) {
        bulkCancelApi({
          organisationId: currentOrg.id,
          escalationIds: selectedEscalationIds,
        });
      }
    }
  }

  function onSnooze(e: DropdownOption) {
    if (!currentOrg?.id) return;

    bulkSnoozeApi({
      organisationId: currentOrg.id,
      escalationIds: selectedEscalationIds,
      snoozeDays: Number(e.value),
    });
  }

  function onFreeze(frozen: boolean) {
    if (!currentOrg?.id) return;

    bulkFreezeApi({
      organisationId: currentOrg.id,
      escalationIds: selectedEscalationIds,
      frozen,
    });
  }

  function onAssign(e: DropdownOption) {
    if (!currentOrg?.id) return;

    bulkAssignApi({
      organisationId: currentOrg.id,
      escalationIds: selectedEscalationIds,
      userId: e.value === UNASSIGN ? null : String(e.value),
    });
  }

  return (
    <div className={ `flex items-center` }>
      <Button
        onClick={ () => onClick(`close`) }
        loading={ bulkCloseLoading }
        size={ `sm` }
        className={ `mr-2` }
        color={ `paidniceGreenOutline` }
        disabled={ disabled }
      >
        { `Close ${escalationCountText}` }
      </Button>

      <Button
        onClick={ () => onClick(`cancel`) }
        loading={ bulkCancelLoading }
        size={ `sm` }
        color={ `paidniceGreenOutline` }
        disabled={ disabled }
      >
        { `Cancel ${escalationCountText}` }
      </Button>

      <Dropdown
        buttonColor={ `white` }
        options={ [
          [{ value: UNASSIGN, label: `Unassign` }],
          userOptions,
        ] }
        onSelect={ onAssign }
        label={ `Assign ${escalationCountText}` }
        size={ `sm` }
        className={ `ml-2` }
        disabled={ disabled }
        loading={ bulkAssignLoading }
        theme={ `outline` }
      />

      <Dropdown
        buttonColor={ `white` }
        options={ [snoozeOptions] }
        onSelect={ onSnooze }
        label={ `Snooze ${escalationCountText}` }
        size={ `sm` }
        className={ `ml-2` }
        disabled={ disabled }
        loading={ bulkSnoozeLoading }
        theme={ `outline` }
      />

      <Button
        onClick={ () => onFreeze(true) }
        loading={ bulkFreezeLoading }
        size={ `sm` }
        className={ `ml-2` }
        color={ `paidniceGreenOutline` }
        disabled={ disabled }
        tooltip={ `Freeze the invoices of the selected invoice escalations. Contact escalations are skipped.` }
      >
        { `Freeze invoices` }
      </Button>

      <Button
        onClick={ () => onFreeze(false) }
        loading={ bulkFreezeLoading }
        size={ `sm` }
        className={ `ml-2` }
        color={ `paidniceGreenOutline` }
        disabled={ disabled }
        tooltip={ `Unfreeze the invoices of the selected invoice escalations. Contact escalations are skipped.` }
      >
        { `Unfreeze invoices` }
      </Button>

      <When condition={ selectedEscalationIds.length > MAX_BATCH }>
        <Paragraph
          className={ `ml-2` }
          variant={ `warning` }
        >
          { `You can only bulk action a maximum of ${MAX_BATCH} escalations at a time.` }
        </Paragraph>
      </When>
    </div>
  );
}
